import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  UserPlus, 
  CreditCard, 
  Sparkles, 
  MessageCircle, 
  Clock,
  Users,
  Gift,
  LogIn
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ActivityItem {
  id: string;
  type: 'signup' | 'payment' | 'ai_usage' | 'ticket' | 'freemium' | 'credit_purchase';
  title: string;
  description: string;
  timestamp: string;
  meta?: string;
}

const ActivityFeed = () => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const [clientsRes, ticketsRes, aiRes] = await Promise.all([
        supabase
          .from('clients')
          .select('id, name, email, plan, paid, amount_paid, created_at, status')
          .order('created_at', { ascending: false })
          .limit(30),
        supabase
          .from('support_tickets')
          .select('id, subject, status, created_at, client_id')
          .order('created_at', { ascending: false })
          .limit(15),
        supabase
          .from('ai_usage_log')
          .select('id, client_id, tool_type, credits_used, created_at')
          .order('created_at', { ascending: false })
          .limit(20),
      ]);

      // Get client names for AI usage and tickets
      const clientIds = new Set<string>();
      (aiRes.data || []).forEach(a => clientIds.add(a.client_id));
      (ticketsRes.data || []).forEach(t => clientIds.add(t.client_id));
      
      const { data: clientNames } = await supabase
        .from('clients')
        .select('id, name')
        .in('id', Array.from(clientIds));
      
      const nameMap = new Map((clientNames || []).map(c => [c.id, c.name]));

      const items: ActivityItem[] = [];

      // Client signups & payments
      (clientsRes.data || []).forEach(c => {
        if (c.paid && c.amount_paid && c.amount_paid > 0) {
          items.push({
            id: `pay-${c.id}`,
            type: 'payment',
            title: `${c.name} purchased ${c.plan}`,
            description: `$${c.amount_paid} payment received`,
            timestamp: c.created_at,
            meta: c.plan,
          });
        } else if (c.plan.toLowerCase() === 'freemium') {
          items.push({
            id: `free-${c.id}`,
            type: 'freemium',
            title: `${c.name} signed up`,
            description: `Freemium account created`,
            timestamp: c.created_at,
          });
        } else {
          items.push({
            id: `signup-${c.id}`,
            type: 'signup',
            title: `${c.name} joined`,
            description: `${c.plan} plan · ${c.email}`,
            timestamp: c.created_at,
            meta: c.plan,
          });
        }
      });

      // Support tickets
      (ticketsRes.data || []).forEach(t => {
        items.push({
          id: `ticket-${t.id}`,
          type: 'ticket',
          title: `New ticket: ${t.subject}`,
          description: `From ${nameMap.get(t.client_id) || 'Unknown'} · ${t.status}`,
          timestamp: t.created_at,
        });
      });

      // AI usage (group credit purchases vs regular usage)
      (aiRes.data || []).forEach(a => {
        if (a.tool_type.startsWith('credit_purchase_')) {
          items.push({
            id: `ai-${a.id}`,
            type: 'credit_purchase',
            title: `Credit pack purchased`,
            description: `${nameMap.get(a.client_id) || 'Unknown'} bought ${a.tool_type.replace('credit_purchase_', '')} pack`,
            timestamp: a.created_at,
          });
        } else {
          items.push({
            id: `ai-${a.id}`,
            type: 'ai_usage',
            title: `AI tool used: ${a.tool_type}`,
            description: `${nameMap.get(a.client_id) || 'Unknown'} · ${a.credits_used} credits`,
            timestamp: a.created_at,
          });
        }
      });

      // Sort by timestamp descending
      items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setActivities(items.slice(0, 50));
    } catch (err) {
      console.error('Error loading activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'payment': return <CreditCard className="w-3.5 h-3.5 text-green-600" />;
      case 'signup': return <UserPlus className="w-3.5 h-3.5 text-blue-600" />;
      case 'freemium': return <LogIn className="w-3.5 h-3.5 text-slate-500" />;
      case 'ai_usage': return <Sparkles className="w-3.5 h-3.5 text-amber-500" />;
      case 'credit_purchase': return <Gift className="w-3.5 h-3.5 text-purple-600" />;
      case 'ticket': return <MessageCircle className="w-3.5 h-3.5 text-orange-500" />;
    }
  };

  const getIconBg = (type: ActivityItem['type']) => {
    switch (type) {
      case 'payment': return 'bg-green-100';
      case 'signup': return 'bg-blue-100';
      case 'freemium': return 'bg-slate-100';
      case 'ai_usage': return 'bg-amber-100';
      case 'credit_purchase': return 'bg-purple-100';
      case 'ticket': return 'bg-orange-100';
    }
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6 bg-white border border-border rounded-2xl shadow-sm">
        <div className="animate-pulse space-y-3">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-muted rounded w-3/4" />
                <div className="h-2 bg-muted rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 bg-white border border-border rounded-2xl shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Activity Feed</h3>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">Live</span>
      </div>
      <ScrollArea className="h-[420px]">
        <div className="space-y-1">
          {activities.map(activity => (
            <div key={activity.id} className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
              <div className={`w-7 h-7 rounded-lg ${getIconBg(activity.type)} flex items-center justify-center shrink-0 mt-0.5`}>
                {getIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{activity.title}</p>
                <p className="text-[10px] text-muted-foreground truncate">{activity.description}</p>
              </div>
              <span className="text-[10px] text-muted-foreground/70 shrink-0 whitespace-nowrap">{formatTime(activity.timestamp)}</span>
            </div>
          ))}
          {activities.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">No activity yet</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ActivityFeed;
